import { useRef } from 'react';
import { Box, Card, Group, Loader, Stack, Text } from '@mantine/core';
import { Target } from 'react-feather';
import type {
  AuthoredAgendaSectionForParticipant,
  ParticipantActiveSessionSlideDeck,
} from '../../../../services/api/participantActiveSession';
import { RichTextContent } from '../../../../components/common/RichTextEditor/RichTextContent';
import { useSlideDeckDoc } from '../../../../components/common/slides/useSlideDeckDoc';
import { SectionSlides } from '../../../../components/common/slides/SectionSlides';
import { resolveMainPage } from '../../../../components/common/slides/resolveMainPage';
import { useFittedSlideWidth } from '../../../../components/common/slides/useFittedSlideWidth';

/**
 * Read-only slide card for the participant active session screen.
 *
 * MED-805 (E3.3) — initial card.
 * MED-976 (AC-PL1/AC-PL2/AC-PL4/AC-PL5) — sourced from the authored model.
 * MED-983 (E3.3a) — layers in the authored Participant Prompt above the slide
 * content and renders the participant's prior-session commitment as a private
 * "Your Last Commitment" callout when there is no slide for the current
 * section. Layout matches the beta visual contract:
 *
 *   Current Focus
 *   ─ Participant Prompt (rich text)
 *   ─ Slide visual                  ← when the section has visuals
 *     OR Your Last Commitment       ← when no visuals + toggle on + prior exists
 *     OR nothing                    ← graceful prompt-only / empty-section case
 *
 * The commitment ENTRY field is rendered by `CommitmentArea` below this card —
 * its gating is driven by `payload.commitment_section_active`, which the
 * backend now derives from `show_commitment_field` + cohort tracking.
 */

export interface ActiveSessionSlideCardProps {
  sections: AuthoredAgendaSectionForParticipant[];
  currentSectionIndex: number | null;
  slideDeck: ParticipantActiveSessionSlideDeck | null;
  /** Needed to fetch the converted deck PDF so the section's slides render. */
  cohortId: string | null;
  sessionId: string | null;
  /**
   * MED-976/E3.3a — the coach-selected "main" slide (1-based page); null ⇒ the
   * section's first slide. Read-only here; the coach drives it.
   */
  currentSlidePage?: number | null;
  /**
   * MED-983 (E3.3a / AC-SLC) — Participant's commitment from the prior session,
   * surfaced when the current section's `show_last_session_commitment` flag and
   * the cohort tracking gate are both on. Already privacy-filtered server-side
   * to the calling participant's own commitment.
   */
  lastSessionCommitment: string | null;
}

export function ActiveSessionSlideCard({
  sections,
  currentSectionIndex,
  slideDeck,
  cohortId,
  sessionId,
  currentSlidePage = null,
  lastSessionCommitment,
}: ActiveSessionSlideCardProps) {
  const section =
    currentSectionIndex !== null
      ? (sections[currentSectionIndex] ?? null)
      : null;
  const sectionName = section?.section_name?.trim() || null;
  const participantPrompt = section?.participant_prompt ?? null;
  const hasParticipantPrompt = !!(
    participantPrompt && participantPrompt.trim() !== ''
  );
  const visuals = section?.visuals ?? [];
  const hasVisuals = visuals.length > 0;

  // E3.3a — render the slide(s) the CM associated with the current section.
  // Load the converted deck once it's ready (persists across section changes);
  // SectionSlides shows the first associated slide large + the rest as thumbs.
  const deckReady = slideDeck?.processing_state === 'ready';
  const { doc, error } = useSlideDeckDoc({
    cohortId,
    sessionId,
    enabled: deckReady,
  });

  // MED-1274 — size the slide to fit the beige mat on BOTH axes rather than the
  // former hard 560px cap (which left the mat a large empty field). Measure the
  // mat's content box (matRef) and the shown page's native aspect ratio; the
  // hook returns the largest width that fits and re-fits on window resize. The
  // coach whiteboard renders the same `SectionSlides` at a fixed width and is
  // untouched — the fitting lives here, on the participant path only.
  const matRef = useRef<HTMLDivElement>(null);
  const mainPage = resolveMainPage(visuals, currentSlidePage);
  const fittedSlideWidth = useFittedSlideWidth({
    doc,
    page: hasVisuals ? mainPage : null,
    containerRef: matRef,
  });
  // Spinner only while a ready deck is genuinely loading; a load failure falls
  // through to the section-name placeholder rather than spinning forever.
  const slidesLoading = hasVisuals && deckReady && !error && !doc;
  const showLastCommitmentCard = !hasVisuals && lastSessionCommitment !== null;
  // Graceful empty: nothing authored for the current section AND no prior
  // commitment to display AND no slide. Keeps the card from rendering a blank
  // frame (AC: "no empty/broken frame").
  const showEmptyState =
    !hasParticipantPrompt && !hasVisuals && !showLastCommitmentCard;
  const total = sections.length;
  const currentDisplay =
    currentSectionIndex !== null ? currentSectionIndex + 1 : null;

  return (
    <Card
      radius="lg"
      padding="xl"
      data-testid="participant-active-slide-card"
      style={{
        backgroundColor: '#FFFFFF',
        border: 'none',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
        // Grow to fill the stage when it has spare height (the stage is a flex
        // column); minHeight keeps the slide mat usable on short content.
        // MDF-243: with no slides, size to the text so the commitment box
        // below is not pushed under the fold by an empty white field.
        minHeight: hasVisuals ? 420 : undefined,
        flex: hasVisuals ? 1 : undefined,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* MDF-241: participants do not see the slide deck's file name. */}
      <Group justify="flex-end" align="center" mb="sm">
        {currentDisplay !== null && total > 0 && (
          <Text
            size="xs"
            c="dimmed"
            data-testid="participant-active-slide-card-counter"
          >
            Section {currentDisplay} of {total}
          </Text>
        )}
      </Group>

      {showEmptyState ? (
        <Stack
          gap="md"
          align="center"
          justify="center"
          style={{ flexGrow: 1, padding: '32px 16px' }}
        >
          <Text c="dimmed" ta="center">
            Waiting for your session leader to advance to the first section…
          </Text>
        </Stack>
      ) : (
        <Stack gap="lg" style={{ flexGrow: 1, padding: '16px 8px' }}>
          <Text
            size="xs"
            tt="uppercase"
            fw={600}
            c="#5A6478"
            style={{ letterSpacing: '0.5px' }}
            data-testid="participant-active-current-focus-label"
          >
            Current Focus
          </Text>

          {hasParticipantPrompt ? (
            <RichTextContent
              content={participantPrompt}
              className="participant-active-prompt"
            />
          ) : (
            sectionName && (
              <Text
                size="lg"
                fw={600}
                style={{ color: '#1A1F36' }}
                data-testid="participant-active-section-name"
              >
                {sectionName}
              </Text>
            )
          )}

          {hasVisuals && (
            // E3.3a — render the section's associated slide(s). The first shows
            // large with any others as thumbnails (SectionSlides). Falls back to
            // the section name while the deck loads or if it isn't convertible,
            // so the area below the prompt is never a broken/empty frame.
            <Stack
              ref={matRef}
              gap="xs"
              align="center"
              justify="center"
              style={{
                flexGrow: 1,
                minHeight: 200,
                padding: '24px 16px',
                backgroundColor: '#F5F3EF',
                borderRadius: 8,
              }}
              data-testid="participant-active-slide-visual"
            >
              {doc ? (
                <SectionSlides
                  doc={doc}
                  visuals={visuals}
                  selectedPage={currentSlidePage}
                  largeWidth={fittedSlideWidth}
                  showFilmstrip={false}
                />
              ) : slidesLoading ? (
                <Loader />
              ) : sectionName ? (
                <Text
                  size="xl"
                  fw={700}
                  ta="center"
                  style={{ color: '#1A1F36' }}
                >
                  {sectionName}
                </Text>
              ) : (
                <Text c="dimmed" ta="center">
                  Slide visual will appear here.
                </Text>
              )}
            </Stack>
          )}

          {showLastCommitmentCard && (
            <Card
              radius="md"
              padding="md"
              data-testid="participant-active-last-commitment-card"
              style={{
                backgroundColor: '#FBF1E5',
                border: '1px solid #EFD9BC',
              }}
            >
              <Stack gap="xs">
                <Group justify="space-between" align="center">
                  <Group gap={8} align="center">
                    <Target size={16} color="#D64545" aria-hidden="true" />
                    <Text fw={600} size="sm" style={{ color: '#1A1F36' }}>
                      Your Last Commitment
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">
                    Only you see this
                  </Text>
                </Group>
                <Text
                  size="sm"
                  style={{ color: '#1A1F36', whiteSpace: 'pre-wrap' }}
                  data-testid="participant-active-last-commitment-text"
                >
                  {lastSessionCommitment}
                </Text>
              </Stack>
            </Card>
          )}
        </Stack>
      )}

      <Box />
    </Card>
  );
}
